import { Store } from '@ngrx/store';
import { map, mergeMap, retry, shareReplay } from 'rxjs/operators';
import { STATIC_MENU_URL_MAP } from './constants';
import { BOTH_DEFAULT_AND_LEGACY_DATA, DEFAULT_ONLY_DATA, LEGACY_ONLY_DATA, } from 'AccountsApp/ts/constants/user-preferences.constants';
import { LEGACY_DATA_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { AjaxService } from '../core/ajax.service';
import { StaticFileUrlService } from '../static-file-url.service';
import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';
import * as i0 from "@angular/core";
import * as i1 from "../core/ajax.service";
import * as i2 from "../static-file-url.service";
import * as i3 from "EntitlementsApp/ts/features/feature-needed/feature.service";
import * as i4 from "AccountsApp/ts/services/accounts.service";
import * as i5 from "@ngrx/store";
/**
 * Static Menu Service
 *
 * A service that fetches menu data stored in .json files (because they are not expected to change).
 */
export class StaticMenuService {
    constructor(ajaxService, staticFileUrlService, featureService, accountsService, store) {
        this.ajaxService = ajaxService;
        this.staticFileUrlService = staticFileUrlService;
        this.featureService = featureService;
        this.accountsService = accountsService;
        this.store = store;
        this.userDataMenuSetting = BOTH_DEFAULT_AND_LEGACY_DATA;
        // Cache
        this.cachedMenuMap = {};
        this.userHasLegacyDataFeature = this.featureService.userHasFeatures([LEGACY_DATA_FEATURE]);
        this.accountsService.getUserPreferences().subscribe((preference) => {
            this.userDataMenuSetting = preference.data_menu_setting;
        });
    }
    /**
     * Given the menu type and group of menu items, get the absolute URL to that file and retrieve the json data.
     * The json data describes the categoriesg in each menu, and any sublist items under those categories.
     *
     * @param menuType The type of menu data that is fetched for i.e. metric menu.
     * @param submenuType A specific group of items under menuType i.e. popular metrics.
     * @param defaultOnly optional param designed to be used if the request is coming from a page that has its own data source override and we are on the default view
     */
    getStaticMenu(menuType, submenuType, defaultOnly) {
        // eslint-disable-next-line no-param-reassign
        defaultOnly = defaultOnly || false;
        const staticJsonUrl = STATIC_MENU_URL_MAP[menuType][submenuType];
        const cachedMenu$ = this.cachedMenuMap[staticJsonUrl]?.[String(defaultOnly)];
        if (cachedMenu$) {
            return cachedMenu$;
        }
        const menu$ = this.staticFileUrlService
            .getAbsoluteURL(staticJsonUrl)
            .pipe(mergeMap((cachedUrl) => {
            return this.ajaxService.getDataObserveBody(cachedUrl, undefined, {
                'Cache-Control': 'max-age=31536000', // Cache for 1 year
            });
        }), retry(3), shareReplay(1))
            .pipe(map((value) => {
            switch (menuType) {
                case 'metricMenu':
                    return this.filterCalcMenuCategories(value, defaultOnly);
                case 'infoFieldMenu':
                    return this.filterInfoFieldMenuCategories(value, defaultOnly);
                default: // if we are retrieving label maps, there is nothing to filter
                    return value;
            }
        }));
        if (!this.cachedMenuMap[staticJsonUrl]) {
            this.cachedMenuMap[staticJsonUrl] = {};
        }
        this.cachedMenuMap[staticJsonUrl][String(defaultOnly)] = menu$;
        return menu$;
    }
    // Filter info field menu categories
    filterInfoFieldMenuCategories(categories, defaultOnly) {
        const filteredCategories = categories.map((c) => ({ ...c, list_items: this.filterMenuItems(c.list_items, defaultOnly) }));
        return filteredCategories.filter((c) => c.list_items.length > 0);
    }
    // Filter calc field menu categories
    filterCalcMenuCategories(categories, defaultOnly) {
        const filteredCategories = categories.map((c) => ({
            ...c,
            sections: this.filterCalcMenuSections(c.sections, c.category_label, defaultOnly),
        }));
        return filteredCategories.filter((c) => c.sections.length > 0);
    }
    /**
     * Filter the calc menu sections.
     *
     * This filters in two ways:
     * 1. First, it filters out complete sections based on legacy data feature and data menu settings:
     *   a. If the data menu settings is BOTH_DEFAULT_AND_LEGACY_DATA, it does not filter the sections here.
     *   c. If the user does not have the legacy data feature, it filters out all sections with `(Morn)` suffix.
     *   b. If the user has the legacy data feature and data menu settings is DEFAULT_ONLY_DATA it filters out all
     *      sections with `(Morn)` suffix.
     *   c. If the user has the legacy data feature and the data menu settings is LEGACY_ONLY_DATA, it filters all
     *      default sections with a `(Morn)` counterpart.
     * 2. Filters sections that end up with no items after filtering the section items.
     *
     * @private
     */
    filterCalcMenuSections(sections, categoryLabel, defaultOnly) {
        let filteredSections;
        if (this.userHasLegacyDataFeature && this.userDataMenuSetting === BOTH_DEFAULT_AND_LEGACY_DATA) {
            // If the user has the legacy data feature and its data menu setting is 'both', then we don't need to filter
            // out any sections
            filteredSections = sections;
            // some menu sections should only be visible to specific client groups
        }
        else if (categoryLabel === 'Firm-Specific Asset Classifications') {
            filteredSections = sections.filter((s) => {
                return this.featureService.userInClientGroup(s.section_label);
            });
        }
        else {
            // We filter out M* sections if the user does not have the legacy data feature or if its data menu setting
            // is default only
            const filterMorningstarSections = !this.userHasLegacyDataFeature || this.userDataMenuSetting === DEFAULT_ONLY_DATA;
            const section_labels = sections.map((s) => s.section_label);
            filteredSections = sections.filter((s) => {
                if (filterMorningstarSections)
                    return !s.section_label.endsWith(' (Morn)');
                return !section_labels.includes(`${s.section_label} (Morn)`);
            });
        }
        const sectionsWithFilteredItems = filteredSections.map((s) => ({ ...s, items: this.filterMenuItems(s.items, defaultOnly) }));
        const nonEmptySections = sectionsWithFilteredItems.filter((s) => s.items.length > 0);
        if (this.userHasLegacyDataFeature && this.userDataMenuSetting === LEGACY_ONLY_DATA)
            return nonEmptySections.map((s) => {
                if (!s.section_label.endsWith(' (Morn)'))
                    return s;
                return { ...s, section_label: s.section_label.slice(0, -7) };
            });
        return nonEmptySections;
    }
    filterMenuItems(items, defaultOnly) {
        return items.filter((i) => {
            // Display element if
            //     always_display is true
            //     of, if the user has legacy data:
            //         if the user data menu setting is legacy only, ignore if is default
            //         if the user data menu setting is default only, ignore if is legacy
            //     or no feature is needed
            //     or user has the required feature_needed
            if (i.always_display)
                return true;
            if (this.userHasLegacyDataFeature) {
                // eslint-disable-next-line eqeqeq
                if (!defaultOnly) {
                    // if there is no context defaultOnly override we go based off data menu setting preference
                    if (this.userDataMenuSetting === LEGACY_ONLY_DATA && i.is_default_version) {
                        return false;
                    }
                    if (this.userDataMenuSetting === DEFAULT_ONLY_DATA &&
                        i.features_needed?.includes(LEGACY_DATA_FEATURE)) {
                        return false;
                    }
                } // if there is a context defaultOnly override we filter out items that need legacy data feature
                else if (i.features_needed?.includes(LEGACY_DATA_FEATURE)) {
                    return false;
                }
            }
            return this.featureService.userCanAccess(i);
        });
    }
}
StaticMenuService.ɵfac = function StaticMenuService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StaticMenuService)(i0.ɵɵinject(i1.AjaxService), i0.ɵɵinject(i2.StaticFileUrlService), i0.ɵɵinject(i3.FeatureService), i0.ɵɵinject(i4.AccountsService), i0.ɵɵinject(i5.Store)); };
StaticMenuService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: StaticMenuService, factory: StaticMenuService.ɵfac, providedIn: 'root' });
